import { useEffect, useState } from "react";
import { useJourneyDynamicDetails, useJourneyStaticDetails } from "../../../context";
import { generateFlightOptions, generatePassengerOptions, generateSSROptions, updateSSR } from "../utils";
import { SSRCursor } from "../context/SSRContext";
import { SSROptionsKeys } from "src/Modules/Flights/constants/JourneyDetails";

export default function useSSR() {
  const { journeyDetails } = useJourneyStaticDetails();
  const { journeySearchData } = useJourneyStaticDetails();
  const { passengersState } = useJourneyDynamicDetails();
  const [passengers, setPassengers] = passengersState;

  const [cursor, setCursor] = useState<SSRCursor>({
    flightId: undefined,
    passengerId: undefined,
    ssrType: undefined,
  });

  const { options: flightOptions, initialOption: initialFlight } = generateFlightOptions(journeySearchData.segments);
  const { options: passengerOptions, initialOption: initialPassenger } = generatePassengerOptions(passengers);
  const { options: SSROptions, initialOption: initialSSR } = generateSSROptions(journeyDetails.data?.ssr);

  useEffect(() => {
    if (journeyDetails.isFetching) return;
    setCursor({
      flightId: initialFlight?.value,
      passengerId: initialPassenger?.value,
      ssrType: initialSSR?.value,
    });
  }, [journeyDetails.isFetching]);

  const handleChange = (id: keyof SSRCursor, value: string) => () => {
    setCursor((prev) => {
      return {
        ...prev,
        [id]: value,
      };
    });
  };
  
  const getSSRItem = (type?: SSROptionsKeys, passengerId?: string) => {
    const flight = flightOptions.find((f) => f.value === cursor.flightId);
    const passengerIdx = passengerOptions.findIndex((p) => p.value === (passengerId ?? cursor.passengerId));

    const ssrOption = SSROptions.find((option) => option.value === type);
    const ssrSelectionType = ssrOption?.ssrSelectionType;

    const passenger = passengers.passengers[passengerIdx];
    const segment = passenger?.segments[flight?.segmentIndex ?? 0];
    const flightItem = segment?.flights[flight?.flightIndex ?? 0];

    if (!segment) return undefined;

    if (ssrSelectionType === "segment") {
      return segment.ssr?.[type ?? "seats"];
    }

    if (ssrSelectionType === "flight") {
      return flightItem?.ssr?.[type ?? "seats"];
    }
    return undefined;
  };

  const updateSSRItem = (type: SSROptionsKeys, value: any) => {
    const flight = flightOptions.find((f) => f.value === cursor.flightId);
    const passengerIdx = passengerOptions.findIndex((p) => p.value === cursor.passengerId);
    const ssr = SSROptions.find((option) => option.value === type);
    setPassengers((prev) => ({
      ...prev,
      passengers: updateSSR(prev, flight, passengerIdx, ssr, value),
    }));
  };

  return {
    cursor,
    flightOptions,
    passengerOptions,
    SSROptions,
    getSSRItem,
    updateSSRItem,
    handleChange,
    isFetching: journeyDetails.isFetching,
  };
}
